import { ReactNode } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import Text from '@cartwhl/ui/base/Text';
import FileIcon from '@cartwhl/ui/icons/FileIcon';

import { navigationStore } from 'stores';

import { IMAGE_EXTENSIONS, MAX_FILE_SIZE } from '../constants';
import { getFileExtensions } from '../utils';

const accept = { 'image/*': IMAGE_EXTENSIONS };

export default function ImageFilePicker({
  children,
}: {
  children?: ReactNode;
}) {
  const handleDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    if (rejectedFiles.length > 0) {
      console.error(
        `Invalid file type. Please upload a ${getFileExtensions(accept)} file.`,
      );
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > MAX_FILE_SIZE) {
        console.error(
          `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
        );
        return;
      }

      navigationStore.setImageUploadModal({
        mode: 'cropping',
        initialSourceImageBlob: file,
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept,
    maxFiles: 1,
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      className={`border-blue relative flex h-full min-h-24 w-full items-center justify-center overflow-hidden rounded-[inherit] border border-dashed p-4 transition-colors duration-200 ease-in-out dark:text-white ${isDragActive ? 'bg-blue/20' : 'bg-blue/10 hover:bg-blue/20'} cursor-pointer`}
    >
      <input {...getInputProps()} />
      {children ? (
        children
      ) : (
        <div className="flex h-full items-center justify-center">
          <div className="flex items-center gap-1.5">
            <FileIcon className="text-blue" />
            <Text variant="studio/body/medium/sm">
              <span className="text-blue">Select File</span>{' '}
              <span className="hidden md:inline">
                or drag and drop one here
              </span>
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
